'use client';

import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { PropsWithChildren, useId, useState } from 'react';
import { MaterialIcon } from '../../../components/MaterialIcon';
import { CmsProduct } from '../../../types/cmsProduct';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../../utils/analytics';
import { useCmsClientContext } from '../../../utils/cmsPageContext';

type ProductDetailsSectionWrapperProps = PropsWithChildren & {
  dropdownSection?: boolean;
  title?: string;
  /** Parent section header, used to build the accordion tracking title. */
  sectionTitle?: string;
  defaultExpanded?: boolean;
  product?: CmsProduct;
  pushDataLayer?: PushDataLayerFn;
};

export const ProductDetailsSectionWrapper: React.FC<
  ProductDetailsSectionWrapperProps
> = ({
  children,
  dropdownSection,
  title,
  sectionTitle,
  defaultExpanded = true,
  product,
  pushDataLayer,
}: ProductDetailsSectionWrapperProps) => {
  const [sectionVisible, setSectionVisible] = useState(defaultExpanded);
  // Unique id linking the toggle button to the panel it controls. The previous static
  // "terms-content" both dangled (the panel had no id) and duplicated across the
  // several accordions a single product renders — useId keeps each pairing valid.
  const panelId = useId();
  const { brand } = useCmsClientContext();
  // Page path as the product-list name, aligned with the rest of the PDP.
  const itemListName = usePathname() ?? undefined;

  // Accordion expand/collapse tracking
  const handleToggle = () => {
    const nextVisible = !sectionVisible;
    setSectionVisible(nextVisible);

    if (product && pushDataLayer && title) {
      sendProductAnalytics({
        event: 'product_interaction',
        action: nextVisible ? UserActionEnum.EXPAND : UserActionEnum.COLLAPSE,
        componentType: ComponentTypeEnum.ACCORDION,
        componentTitle: `Product Details Module/${
          sectionTitle ? `${sectionTitle}_` : ''
        }${title}`,
        itemListName,
        product,
        index: '0',
        pushDataLayer,
      });
    }
  };

  const sectionTitleStyles = classNames('text-left', {
    'text-text/accent mhg-alpha-tag-font':
      brand === 'singsaver' || brand === 'moneymax',
    ' mhg-alpha-typography-body-1-relaxed':
      brand === 'money101' || brand === 'moneyhero',
  });

  return (
    // eslint-disable-next-line tailwindcss/no-arbitrary-value
    <div className="rounded-radius/card bg-white border-[#01000A14] product-details-shadow">
      {dropdownSection && (
        // Sub-section title as an <h3> wrapping the accordion toggle (WAI-ARIA
        // accordion pattern) so it contributes to the heading outline (h2 > h3).
        <h3 className="py-spacing/padding/y px-spacing/padding/x m-0">
          <button
            type="button"
            className="flex w-full cursor-pointer items-center justify-between"
            onClick={handleToggle}
            aria-expanded={sectionVisible}
            aria-controls={panelId}
          >
            <span className={sectionTitleStyles}>{title}</span>
            <MaterialIcon
              icon="expand_less"
              className={`transition-transform ${sectionVisible ? 'rotate-0' : 'rotate-180'}`}
            />
          </button>
        </h3>
      )}
      <div
        id={panelId}
        className={classNames({ hidden: dropdownSection && !sectionVisible })}
      >
        {children}
      </div>
    </div>
  );
};
